import { formatAddress } from '@medplum/core'
import type { Address } from '@medplum/fhirtypes'
import type { AddressSuggestion } from '@seen/request-types/addressLookup'
import { Button, cn } from '@seen/ui'
import { IconMapPin, IconPencil, IconX } from '@tabler/icons-react'
import { type JSX, useCallback, useState } from 'react'
import { AddressConfirmDialog } from '#src/components/address/AddressConfirmDialog.tsx'
import {
  ComboboxAsync,
  type ComboboxAsyncOption,
} from '#src/components/core/combobox/ComboboxAsync.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'

// Google returns nothing useful for fewer characters, and each call is billed.
const MIN_SEARCH_LENGTH = 3

interface AddressDraft {
  placeId?: string
  address?: Address
}

export interface AddressAutocompleteProps {
  value?: Address
  onChange: (address: Address | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

function SuggestionLabel({
  suggestion,
}: {
  suggestion: AddressSuggestion
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate">{suggestion.mainText}</span>
      {suggestion.secondaryText && (
        <span className="truncate text-xs text-muted-foreground">
          {suggestion.secondaryText}
        </span>
      )}
    </div>
  )
}

function getSuggestionValue(suggestion: AddressSuggestion): string {
  return suggestion.placeId
}

export function AddressAutocomplete({
  value,
  onChange,
  placeholder = 'Search for an address',
  disabled = false,
  className,
}: AddressAutocompleteProps): JSX.Element {
  const { ai } = useSeenApi()
  // One token spans the keystrokes and the Place Details call that ends them,
  // which Google bills as a single session.
  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID())
  const [draft, setDraft] = useState<AddressDraft | null>(null)

  const loadOptions = useCallback(
    async ({
      input,
      signal,
    }: {
      input: string
      signal: AbortSignal
    }): Promise<ComboboxAsyncOption<AddressSuggestion>[]> => {
      const search = input.trim()
      if (!ai || search.length < MIN_SEARCH_LENGTH) {
        return []
      }
      const { suggestions } = await ai.addressAutocomplete(
        { input: search, sessionToken },
        { signal }
      )
      return suggestions.map((suggestion) => ({
        value: suggestion,
        label: <SuggestionLabel suggestion={suggestion} />,
        keywords: [suggestion.text],
      }))
    },
    [ai, sessionToken]
  )

  const closeDraft = (): void => {
    setDraft(null)
    setSessionToken(crypto.randomUUID())
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <ComboboxAsync<AddressSuggestion>
        queryKey={['address-autocomplete', sessionToken]}
        loadOptions={loadOptions}
        getValue={getSuggestionValue}
        sourceRanked
        searchPlaceholder={
          ai ? 'Start typing an address…' : 'Type the street address…'
        }
        onSelect={(suggestion) =>
          suggestion &&
          setDraft({ placeId: suggestion.placeId, address: value })
        }
        createLabel={(search) => (
          <span className="text-muted-foreground">
            Enter “{search}” manually
          </span>
        )}
        // A typed address starts a new postal address: only the non-postal
        // metadata carries over, not the previous city, state or ZIP.
        onCreate={(search) =>
          setDraft({
            address: {
              use: value?.use,
              type: value?.type,
              period: value?.period,
              line: [search.trim()],
            },
          })
        }
        alwaysShowCreate
        className="w-(--radix-popover-trigger-width) min-w-80"
      >
        <Button
          variant="outline"
          disabled={disabled}
          aria-label={value ? 'Change address' : placeholder}
          className="min-w-0 flex-1 justify-start gap-2 font-normal"
        >
          <IconMapPin className="size-4 shrink-0 text-muted-foreground" />
          {value ? (
            <span className="truncate">
              {formatAddress(value) || value.text}
            </span>
          ) : (
            <span className="truncate text-placeholder">{placeholder}</span>
          )}
        </Button>
      </ComboboxAsync>
      {value && !disabled && (
        <>
          <Button
            variant="outline"
            size="icon"
            aria-label="Edit address"
            onClick={() => setDraft({ address: value })}
          >
            <IconPencil className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label="Clear address"
            onClick={() => onChange(undefined)}
          >
            <IconX className="size-4" />
          </Button>
        </>
      )}
      {draft && (
        <AddressConfirmDialog
          address={draft.address}
          placeId={draft.placeId}
          sessionToken={sessionToken}
          title={draft.placeId || !value ? 'Confirm address' : 'Edit address'}
          onCancel={closeDraft}
          onSave={(address) => {
            onChange(address)
            closeDraft()
          }}
        />
      )}
    </div>
  )
}
